import LoadingButton from "@/components/common/form/loading-button";
import Modal from "@/components/common/ui/modal";
import { GET_ORGANIZATIONS, UPDATE_ORGANIZATION } from "@/gql/organization";
import { IOrganization } from "@/types/organization";
import { useMutation } from "@apollo/client";
import {
  IUpdateOrganizationMutation,
  IUpdateOrganizationMutationVariables,
} from "@shared/common/types/api-private/generated/graphql";
import { ISite } from "@shared/common/types/site";
import { Form, Input } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import FormLabel from "../auth/form-label";
import InstallScriptLoading from "./onboarding/install-script-loading";
import NhnOnboardingScript from "./onboarding/nhn-onboarding-script";
import OnboardingGuide from "./onboarding/onboarding-guide";
import OnboardingLoading from "./onboarding/onboarding-loading";

interface INhnOnboardingModalProps {
  organizations: IOrganization[];
  currentSite: ISite | null;
  currentOrganization: IOrganization;
  onComplete: () => Promise<void>;
  showOnboarding: boolean;
}

interface ISiteFormValues {
  url: string;
  name: string;
}

const NhnOnboardingModal: React.FC<INhnOnboardingModalProps> = ({
  currentOrganization,
  currentSite,
  onComplete,
  showOnboarding,
}) => {
  const [open, setOpen] = useState(showOnboarding);
  const [step, setStep] = useState(
    currentOrganization.onboardStep === 50
      ? 1
      : currentOrganization.onboardStep === 60
      ? 2
      : 3
  );

  const [updateLoading, setUpdateLoading] = useState(false);

  const [form] = Form.useForm<ISiteFormValues>();

  const [updateTeam] = useMutation<
    IUpdateOrganizationMutation,
    IUpdateOrganizationMutationVariables
  >(UPDATE_ORGANIZATION, { refetchQueries: [GET_ORGANIZATIONS] });

  const handleFormFinish = useCallback(async () => {
    setStep(1.5);

    try {
      setUpdateLoading(true);

      // step 변경
      await Promise.all([
        new Promise((resolve) => setTimeout(resolve, 2500)),
        updateTeam({
          variables: {
            organizationId: currentOrganization.id,
            object: { onboardStep: 60 },
          },
        }),
      ]);

      setStep(2);
    } finally {
      setUpdateLoading(false);
    }
  }, [currentOrganization.id, updateTeam]);

  const handleClose = useCallback(async () => {
    await onComplete();
    setOpen(false);
  }, [onComplete]);

  useEffect(() => {
    setOpen(showOnboarding);
  }, [currentOrganization, showOnboarding]);

  return (
    <Modal
      open={open}
      title={
        step === 1
          ? "사이트 추가"
          : step === 2
          ? "코드앤버터 스크립트 설치"
          : "코드앤버터를 사용할 준비가 끝났어요!"
      }
      headerRight={
        <span className="px-2 py-0.5 text-xs rounded-full bg-cb-gray-100 text-cb-gray-800">
          {step}/3
        </span>
      }
      backdrop
      hideCloseButton
      hideFooter
    >
      {step === 1 || step === 1.5 ? (
        <>
          <Form
            form={form}
            requiredMark={false}
            layout="vertical"
            initialValues={{
              name: currentSite?.name ?? "",
              url: currentSite?.url ?? "",
            }}
            onFinish={handleFormFinish}
          >
            <div className="text-center text-cb-gray-700">
              <div className="mt-8 text-left w-104">
                <Form.Item
                  name="name"
                  label={<FormLabel label="사이트명" required={step === 1} />}
                  className={twMerge("mb-6")}
                  rules={[
                    {
                      required: step === 1,
                      message: "사이트명을 입력해주세요.",
                    },
                  ]}
                >
                  <Input
                    disabled
                    placeholder="사이트명을 입력해주세요."
                    className="w-full text-base text-black disabled:text-black"
                  />
                </Form.Item>

                <Form.Item
                  name="url"
                  label={<FormLabel label="도메인" required />}
                  rules={[
                    { required: true, message: "도메인을 입력해주세요." },
                  ]}
                >
                  <Input
                    disabled
                    placeholder="도메인을 입력해주세요."
                    className="w-full text-base text-black disabled:text-black"
                  />
                </Form.Item>

                <LoadingButton
                  type="submit"
                  theme="primary"
                  className="mt-8"
                  block
                  loading={step === 1 && updateLoading}
                  data-gtm-id="nhn_onboarding_site_add"
                >
                  추가
                </LoadingButton>
              </div>
            </div>
          </Form>

          {step === 1.5 ? <OnboardingLoading /> : <></>}
        </>
      ) : (
        <></>
      )}

      {step === 2 || step === 2.5 ? (
        <>
          <NhnOnboardingScript
            currentOrganization={currentOrganization}
            publicId={currentSite?.publicId || ""}
            onChangeStep={setStep}
          />
          {step === 2.5 ? <InstallScriptLoading /> : <></>}
        </>
      ) : (
        <></>
      )}

      {step === 3 ? (
        <OnboardingGuide
          currentOrganization={currentOrganization}
          setStep={setStep}
          onClose={handleClose}
        />
      ) : (
        <></>
      )}
    </Modal>
  );
};

export default NhnOnboardingModal;
